import { useState } from "react";
import {
  Space,
  Table,
  Tag,
  Popconfirm,
  Switch,
  Pagination,
  Modal,
  Input,
  Form,
  Button,
  message,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { IoTrashOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { BiEdit } from "react-icons/bi";
import { RiLockPasswordLine } from "react-icons/ri";
import { PUT } from "../../../utils/apirequest";
import { API } from "../../../config/API";
import { countryLabel } from "../../../config/countries";
import { regionLabel } from "../../../config/regions";

function Datatable(props: any) {
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const navigate = useNavigate();
  const [passwordModal, setPasswordModal] = useState<any>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [pwForm] = Form.useForm();
  const [pwValue, setPwValue] = useState("");

  const passwordRules = [
    { label: "Minimum 8 characters", test: (v: string) => v.length >= 8 },
    { label: "At least one lowercase letter", test: (v: string) => /[a-z]/.test(v) },
    { label: "At least one uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
    { label: "At least one number", test: (v: string) => /\d/.test(v) },
    { label: "At least one special character", test: (v: string) => /[!@#$%^&*()_+[\]{};':"\\|,.<>?]/.test(v) },
  ];

  const allPwRulesPassed = pwValue ? passwordRules.every((r) => r.test(pwValue)) : false;

  const handlePasswordUpdate = async () => {
    try {
      const values = await pwForm.validateFields();
      setPasswordLoading(true);
      let url = API.PUT_UPDATE_USER + `${passwordModal.id}`;
      await PUT(url, { password: values.password });
      message.success("Password updated successfully");
      setPasswordModal(null);
      pwForm.resetFields();
      setPwValue("");
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error("Failed to update password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChange = (pagination: any, filters: any, sorter: any) => {
    setSortedInfo(sorter);
  };

  const handleSwitchChange = (checked: boolean, id: number) => {
    props.onUpdate(id, checked);
  };

  interface DataType {
    key: string;
    id: number;
    username: string;
    email: string;
    phone_number: string;
    epaper_users_count: number;
    epaper_subscriptions_count: number;
    blocked: boolean;
  }

  const columns: ColumnsType<DataType> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 50,
      render: (text, record, index) => {
        return <div>{index + 1}</div>;
      },
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 250,
      sorter: (a: any, b: any) => a.username.localeCompare(b.username),
      sortOrder: sortedInfo.columnKey === "username" && sortedInfo.order,
      render: (text, record: any) => {
        return (
          <div
            style={{ cursor: "pointer", color: "#0576b7" }}
            onClick={() => navigate(`/Auth/agents/view/${record.id}`)}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 320,
      render: (text, record: any) => {
        return (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => navigate(`/Auth/agents/view/${record.id}`)}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 120,
      render: (text: any) => {
        return <div>{text || "-"}</div>;
      },
    },
    {
      title: "Phone",
      dataIndex: "phone_number",
      key: "phone_number",
      width: 150,
    },
    {
      title: "Registered Users",
      dataIndex: "epaper_users_count",
      key: "epaper_users_count",
      width: 140,
      sorter: (a: any, b: any) =>
        a.epaper_users_count - b.epaper_users_count,
      sortOrder:
        sortedInfo.columnKey === "epaper_users_count" && sortedInfo.order,
      render: (text: number, record: any) => {
        return (
          <Tooltip
            title={
              text > 0
                ? "View users registered by this agent"
                : "No registered users yet"
            }
          >
            <Tag
              color={text > 0 ? "blue" : "default"}
              style={{ cursor: "pointer" }}
              onClick={() =>
                navigate(
                  `/Auth/agents/view/${record.id}?tab=registered`
                )
              }
            >
              {text}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Subscribed Users",
      dataIndex: "epaper_subscriptions_count",
      key: "epaper_subscriptions_count",
      width: 140,
      sorter: (a: any, b: any) =>
        (a.epaper_subscriptions_count || 0) -
        (b.epaper_subscriptions_count || 0),
      sortOrder:
        sortedInfo.columnKey === "epaper_subscriptions_count" &&
        sortedInfo.order,
      render: (text: number, record: any) => {
        const count = text || 0;
        return (
          <Tooltip
            title={
              count > 0
                ? "View subscriptions recorded by this agent"
                : "No subscriptions recorded yet"
            }
          >
            <Tag
              color={count > 0 ? "green" : "default"}
              style={{ cursor: "pointer" }}
              onClick={() =>
                navigate(
                  `/Auth/agents/view/${record.id}?tab=subscribers`
                )
              }
            >
              {count}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      width: 140,
      render: (text: string) => (
        <span style={{ fontSize: 13, whiteSpace: "nowrap" }}>
          {countryLabel(text) || "-"}
        </span>
      ),
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      width: 100,
      // A region code or "Others"; older agents have a country code or
      // "india"/"gulf".
      render: (text: string) => {
        const code = String(text || "").toLowerCase();
        return (
          <Tag color={code === "gulf" ? "orange" : code === "india" ? "blue" : "default"}>
            {regionLabel(text) || "-"}
          </Tag>
        );
      },
    },
    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      width: 220,
      render: (text: string) => {
        return (
          <span style={{ fontSize: 13, whiteSpace: "nowrap" }}>
            {text || "-"}
          </span>
        );
      },
    },
    {
      title: "District",
      dataIndex: "district",
      key: "district",
      width: 220,
      // If the agent picked "Others", show the operator-entered custom
      // district name — never the literal "Others" label.
      render: (_: string, record: any) => {
        const display =
          record?.district === "Others"
            ? record?.custom_district || "Others"
            : record?.district || "-";
        return (
          <span style={{ fontSize: 13, whiteSpace: "nowrap" }}>{display}</span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (text, record: any) => {
        return (
          <Tag
            color={record?.blocked ? "red" : "green"}
            style={{ fontSize: "10px" }}
          >
            {record?.blocked ? "Inactive" : "Active"}
          </Tag>
        );
      },
    },
    {
      title: "Blocked",
      key: "blocked",
      width: 70,
      render: (text, record: any) => {
        return (
          <Switch
            checked={record?.blocked}
            loading={props.blockingId === record?.id}
            onChange={(val) => handleSwitchChange(val, record?.id)}
          />
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      width: 160,
      fixed: "right" as const,
      render: (_, record) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-evenly",
              alignContent: "center",
            }}
          >
            <Space size="middle" style={{ marginRight: 10 }}>
              <BiEdit
                size={20}
                color="#f5a442"
                cursor={"pointer"}
                onClick={() => navigate(`/Auth/agents/form/${record.id}`)}
              />
            </Space>
            <Space size="middle" style={{ marginRight: 10 }}>
              <Tooltip title="Change Password">
                <RiLockPasswordLine
                  size={20}
                  color="#0055a6"
                  cursor={"pointer"}
                  onClick={() => {
                    setPasswordModal(record);
                    pwForm.resetFields();
                    setPwValue("");
                  }}
                />
              </Tooltip>
            </Space>
            <Popconfirm
              title="Delete the agent"
              description="Are you sure to delete this agent?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => props.onDelete(record.id)}
            >
              <Space size="middle">
                <IoTrashOutline size={20} color="red" cursor={"pointer"} />
              </Space>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ marginTop: 10 }}>
      <Table
        columns={columns}
        dataSource={props.data}
        pagination={false}
        onChange={handleChange}
        size="small"
        rowKey="id"
        scroll={{ x: "max-content" }}
      />
      <br />
      <div style={{ float: "right" }}>
        <Pagination
          responsive
          defaultCurrent={Number(props?.page) || 1}
          total={props.meta || 0}
          pageSize={Number(props?.pagesize) || 10}
          onChange={(p, t) => {
            props.onPageChange(p, t);
          }}
        />
      </div>
      <Modal
        title={`Change Password — ${passwordModal?.username}`}
        open={!!passwordModal}
        onCancel={() => { setPasswordModal(null); pwForm.resetFields(); setPwValue(""); }}
        onOk={handlePasswordUpdate}
        okText="Update Password"
        confirmLoading={passwordLoading}
        okButtonProps={{ disabled: !allPwRulesPassed }}
      >
        <Form form={pwForm} layout="vertical">
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              {
                validator: (_: any, value: any) => {
                  if (!value) return Promise.reject("Please enter a password");
                  if (passwordRules.every((r) => r.test(value))) return Promise.resolve();
                  return Promise.reject("");
                },
              },
            ]}
            style={pwValue && !allPwRulesPassed ? { marginBottom: 4 } : {}}
            help={pwValue && !allPwRulesPassed ? "" : undefined}
          >
            <Input.Password
              size="large"
              placeholder="Enter new password"
              onChange={(e) => setPwValue(e.target.value)}
            />
          </Form.Item>
          {pwValue && !allPwRulesPassed && (
            <div style={{ marginBottom: 12 }}>
              {passwordRules.map((rule, i) => {
                const passed = rule.test(pwValue);
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "2px 0",
                      fontSize: 13,
                      color: passed ? "#52c41a" : "#999",
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{passed ? "✓" : "✕"}</span>
                    {rule.label}
                  </div>
                );
              })}
            </div>
          )}
          <Form.Item
            name="confirm"
            label="Confirm Password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value)
                    return Promise.resolve();
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password size="large" placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Datatable;
